import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  Button,
  Tooltip,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  InputLabel,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { MIconButton } from '@/@material-extend';
import { useFormik } from 'formik';
import moment from 'moment';
import { makeStyles } from '@mui/styles';

import CONFIG from '@/config';
import { NOTE_ACTIONS, StaffNote } from './Types';
import { getRestHeaders } from '@/common/utils/api';
import { CloseIcon } from '@/common/icons/CloseIcon';
import { formDataInstance } from '@/common/axios';
import { getErrorDetails } from './ApiErrorLookup';
import CustomDatePickerField from '@/components/CustomDatePicker';
import { useCustomerNoteEnums } from '@/hooks/useCustomerNoteEnums';
import { AddEditNote } from '@/schemas/addEditNote';
import { getNowEpoch } from '@/common/utils/time';
import { useActiveStoreStaff } from '@/hooks/useActiveStoreStaff';

const useStyles: any = makeStyles(() => ({
  addEditNoteModal: {
    '& .MuiPaper-root': {
      maxWidth: 900
    }
  }
}));

export interface AddNoteApiResponse {
  data: {
    data: {
      note: StaffNote;
      success?: boolean;
    };
  };
}

export interface AddEditNoteModalProps {
  isOpen: boolean;
  note?: StaffNote;
  customerId: number;
  titleTextOverride?: string;
  setIsOpen: (isOpen: boolean) => void;
  actionCallback?: (note: StaffNote, action: keyof typeof NOTE_ACTIONS) => void;
}

export const apiSuffix = '/staff/user-note';

const IMPORTED_FROM_MINDBODY_CATEGORY = 14;

const AddEditNoteModal = ({
  isOpen = false,
  note,
  customerId,
  titleTextOverride,
  setIsOpen,
  actionCallback = () => {}
}: AddEditNoteModalProps) => {
  const isEditing = note !== null;

  const classes = useStyles();
  const { enums, loading } = useCustomerNoteEnums();
  const { items: staffForSelection } = useActiveStoreStaff();

  const categoryForSelection = useMemo(() => {
    if (enums) {
      return note?.categoryId === IMPORTED_FROM_MINDBODY_CATEGORY
        ? enums.noteCategories
        : enums.noteCategories.filter(
            i => i.id !== IMPORTED_FROM_MINDBODY_CATEGORY
          );
    } else {
      return [];
    }
  }, [enums]);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [initialFocusSet, setInitialFocusSet] = useState<boolean>(false);
  const [assigneeActorType, setAssigneeActorType] = useState<string | null>(
    'STAFF'
  );

  // if a user that the note is assigned to is no longer active we need to add it to the
  // set of staff in the list so it will render the current value:
  const staff = useMemo(() => {
    if (staffForSelection.findIndex(s => s.id === note?.assignee?.id) !== -1) {
      return staffForSelection;
    } else {
      if (note?.assignee) {
        return [...staffForSelection, note?.assignee];
      } else {
        return staffForSelection;
      }
    }
  }, [staffForSelection, note]);

  const nowEpoch = getNowEpoch();
  const formik = useFormik({
    initialValues: isEditing
      ? {
          noteId: note.id,
          customerId: customerId,
          categoryId: note.categoryId,
          content: note.content,
          status: note.status,
          followupDate: note.followupDate ? note.followupDate : null,
          followupTime: note?.followupTime,
          assignee: note?.assignee?.id,
          contactedVia: note?.contactedVia,
          hasFollowUp: Boolean(note?.followupDate)
        }
      : {
          customerId: customerId,
          categoryId: null,
          content: '',
          status: null,
          followupDate: null,
          followupTime: null,
          assignee: null,
          contactedVia: null,
          hasFollowUp: false
        },
    validationSchema: AddEditNote,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    onSubmit: values => {
      const params = {
        customerId: customerId,
        categoryId: values.categoryId,
        content: values.content,
        status: values.status,
        assignee: values.assignee,
        contactedVia: values.contactedVia,
        actorType: assigneeActorType
      };
      if (values.noteId) {
        params['noteId'] = values.noteId;
      }
      if (values.hasFollowUp) {
        params['followupTime'] = formik.values.followupTime;
        params['followupDate'] = moment(formik.values.followupDate).format(
          'MM/DD/YYYY'
        );
      } else {
        params['followupDate'] = null;
        params['followupTime'] = null;
      }

      const method = isEditing ? 'patch' : 'post';
      formDataInstance[method](CONFIG.USERS_API_URL + apiSuffix, params, {
        headers: getRestHeaders()
      })
        .then((response: AddNoteApiResponse) => {
          const updatedNote = response.data.data.note;
          if (isEditing) {
            actionCallback(updatedNote, NOTE_ACTIONS.UPDATE_NOTE);
          } else {
            actionCallback(updatedNote, NOTE_ACTIONS.INSERT_NOTE);
          }
          enqueueSnackbar(`Note Saved`, {
            variant: 'success',
            action: (key: any) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)} />
            )
          });
          setIsOpen(false);
        })
        .catch(error => {
          enqueueSnackbar(
            getErrorDetails(
              `There was a problem ${isEditing ? 'Saving' : 'Adding'} Note.`,
              error
            ),
            {
              variant: 'error',
              action: (key: any) => (
                <MIconButton size="small" onClick={() => closeSnackbar(key)} />
              )
            }
          );
        })
        .finally(() => formik.setSubmitting(false));
    }
  });

  // need to update the actor type to send to PATCH as it is changed, might be a sales_rep or staff
  useEffect(() => {
    const selectedStaff = staff.find(s => s.id === formik.values.assignee);
    if (selectedStaff) {
      setAssigneeActorType(selectedStaff.actorType);
    } else {
      setAssigneeActorType(null);
    }
  }, [formik.values.assignee]);

  const title = titleTextOverride || (isEditing ? 'Edit Note' : 'Add Note');

  return (
    <Dialog
      fullWidth
      open={isOpen}
      onClose={() => setIsOpen(false)}
      className={classes.addEditNoteModal}
    >
      <DialogTitle
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 15px 10px 24px',
          justifyContent: 'space-between',
          fontWeight: 600
        }}
      >
        {title}
        <Tooltip arrow title="Close">
          <IconButton onClick={() => setIsOpen(false)} size="large">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        {!loading && (
          <>
            <Grid container>
              <Grid xs={12} item>
                <InputLabel>Note</InputLabel>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  inputRef={input => {
                    if (!initialFocusSet) {
                      setInitialFocusSet(true);
                      return input && input.focus();
                    }
                  }}
                  name="content"
                  value={formik.values.content}
                  onChange={e =>
                    formik.setFieldValue('content', e.target.value)
                  }
                  inputProps={{ maxLength: 4096 }}
                />
              </Grid>
              <Grid xs={12} item mt={2}>
                <InputLabel>Category</InputLabel>
                <TextField
                  select
                  fullWidth
                  name="category"
                  value={formik.values.categoryId}
                  onChange={e => {
                    // preventing selection of "Imported from MindBody"
                    const catId = parseInt(e.target.value);
                    if (catId !== 14) {
                      formik.setFieldValue('categoryId', catId);
                    }
                  }}
                >
                  {categoryForSelection?.map(option => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            {formik.values.categoryId !== null && (
              <Grid container>
                <Grid xs={12} item mt={2}>
                  <InputLabel>Status</InputLabel>
                  <TextField
                    select
                    fullWidth
                    name="status"
                    onChange={e =>
                      formik.setFieldValue('status', e?.target?.value)
                    }
                    value={formik.values.status}
                  >
                    {enums?.status?.map(s => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid xs={12} item mt={2}>
                  <InputLabel>Assigned To</InputLabel>
                  <TextField
                    select
                    fullWidth
                    name="assignee"
                    onChange={e =>
                      formik.setFieldValue('assignee', e?.target?.value)
                    }
                    value={formik.values?.assignee}
                  >
                    {staff?.map(s => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.firstName} {s.lastName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid xs={12} item mt={2}>
                  <InputLabel>Contacted Via</InputLabel>
                  <TextField
                    select
                    fullWidth
                    name="contactedVia"
                    onChange={e =>
                      formik.setFieldValue('contactedVia', e?.target?.value)
                    }
                    value={formik.values.contactedVia}
                  >
                    {enums?.contactedVia?.map(s => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid xs={12} item mt={2}>
                  <FormControlLabel
                    label={'Follow-Up'}
                    control={
                      <Checkbox
                        color="primary"
                        name="hasFollowUp"
                        checked={formik.values.hasFollowUp}
                        onChange={e => {
                          const checked = e.target.checked;
                          formik.setFieldValue('hasFollowUp', checked);
                          if (checked) {
                            formik.setFieldValue(
                              'followupDate',
                              new Date().valueOf()
                            );
                            formik.setFieldValue('followupTime', 0);
                          }
                        }}
                      />
                    }
                  />
                </Grid>
                <Grid xs={12} item mt={2}>
                  <InputLabel>Follow-Up Date</InputLabel>
                  {formik.values.hasFollowUp && (
                    <CustomDatePickerField
                      name="followupDate"
                      required
                      dateFormat="MM/dd/yyyy"
                      value={moment(formik.values.followupDate)}
                      minDate={nowEpoch}
                      onChangeHandler={date =>
                        formik.setFieldValue('followupDate', date.valueOf())
                      }
                      showTimeSelect={false}
                    />
                  )}
                  {!formik.values.hasFollowUp && (
                    <TextField
                      fullWidth
                      name="followupTime"
                      onChange={e =>
                        formik.setFieldValue('followupTime', e?.target?.value)
                      }
                      value={''}
                      disabled={true}
                    />
                  )}
                </Grid>
                <Grid xs={12} item mt={2}>
                  <InputLabel>Follow-Up Time</InputLabel>
                  <TextField
                    select
                    fullWidth
                    name="followupTime"
                    onChange={e =>
                      formik.setFieldValue('followupTime', e?.target?.value)
                    }
                    value={
                      formik.values.hasFollowUp
                        ? formik.values.followupTime
                        : ''
                    }
                    disabled={!formik.values.hasFollowUp}
                  >
                    {enums?.followupTimes?.map(option => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.description}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>
        <LoadingButton
          onClick={formik.submitForm}
          variant="contained"
          disabled={!formik.isValid}
          loading={formik.isSubmitting}
        >
          {isEditing ? 'Save' : 'Add'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditNoteModal;
